<script lang="ts">
	import DeliveryCountrySelect from "@components/DeliveryCountrySelect.svelte";
	import Logo from "@components/Logo.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { setCookie } from "@utils/setCookie.js";
	import facebookIcon from "@core/assets/icons/facebook.svg?raw";
	import instagramIcon from "@core/assets/icons/instagram.svg?raw";
	import youtubeIcon from "@core/assets/icons/youtube.svg?raw";
	import type { Country } from "@core/schema/Country.js";
	import { Locale, type LocalizedString } from "@core/schema/Locale.js";
	import { fathersContact } from "@core/schema/fathersCompany.js";
	import { getAboutUsPath } from "@core/schema/paths/getAboutUsPath.js";
	import { getContactPath } from "@core/schema/paths/getContactPath.js";
	import { getCookiesPolicyPath } from "@core/schema/paths/getCookiesPolicyPath.js";
	import { getDeliveryAndPaymentPath } from "@core/schema/paths/getDeliveryAndPaymentPath.js";
	import { getGdprPath } from "@core/schema/paths/getGdprPath.js";
	import { getGiftSubscriptionPath } from "@core/schema/paths/getGiftSubscriptionPath.js";
	import { getHomepagePath } from "@core/schema/paths/getHomepagePath.js";
	import { getNewsAnchor } from "@core/schema/paths/getNewsAnchor.js";
	import { getPostsListingPath } from "@core/schema/paths/getPostsListingPath.js";
	import { getSubcategoryPath } from "@core/schema/paths/getSubcategoryPath.js";
	import { getSubscriptionPagePath } from "@core/schema/paths/getSubscriptionPagePath.js";
	import { getTermsOfServicePath } from "@core/schema/paths/getTermsOfServicePath.js";
	import { getWholesalePath } from "@core/schema/paths/getWholesalePath.js";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { defaultDestinationCountry } from "../../defaultDestinationCountry.js";
	import { destinationCountryCookieName } from "../../destinationCountryCookieName.js";
	import { selectedDestinationCountry } from "../../state/selectedDestinationCountry.svelte.js";
	import CurrencySwitcher from "./CurrencySwitcher.svelte";
	import translations from "./Footer.translations.json" with { type: "json" };
	import LanguageSwitcher from "./LanguageSwitcher.svelte";

	const translate = getTranslate(translations);

	const { tenant, subcategories } = getGlobalTemplateProps();
	type FooterMenu = { link: string; label: LocalizedString }[];

	const {
		pageHasMobileBottomBar = false,
	}: {
		pageHasMobileBottomBar?: boolean;
	} = $props();

	const footerLinks: FooterMenu = [
		{
			link: getTermsOfServicePath(tenant),
			label: { [Locale.cs]: "Obchodní podmínky", [Locale.en]: "Terms of Service" },
		},
		{
			link: getDeliveryAndPaymentPath(tenant),
			label: { [Locale.cs]: "Doprava a platba", [Locale.en]: "Delivery & Payment" },
		},
		{ link: getCookiesPolicyPath(tenant), label: { [Locale.cs]: "Cookies zásady", [Locale.en]: "Cookies Policy" } },
		{ link: getGdprPath(tenant), label: { [Locale.cs]: "Ochrana osobních údajů", [Locale.en]: "Data Protection" } },
	];

	const footerMenu: Record<string, FooterMenu> = {
		Shop: [
			{
				link: getHomepagePath(tenant) + getNewsAnchor(tenant.locale),
				label: { [Locale.cs]: "Novinky", [Locale.en]: "News" },
			},
			...subcategories.slice(0, 4).map((subcategory) => ({
				link: getSubcategoryPath(tenant.locale, subcategory),
				label: subcategory.name,
			})),
		],
		[{ [Locale.cs]: "Ostatní", [Locale.en]: "Others" }[tenant.locale]]: [
			{ link: getSubscriptionPagePath(tenant), label: { [Locale.cs]: "Předplatné", [Locale.en]: "Subscription" } },
			{
				link: getGiftSubscriptionPath(tenant),
				label: { [Locale.cs]: "Darujte předplatné", [Locale.en]: "Gift a Subscription" },
			},
			{ link: getWholesalePath(tenant), label: { [Locale.cs]: "B2B", [Locale.en]: "B2B" } },
			{ link: getAboutUsPath(tenant), label: { [Locale.cs]: "O nás", [Locale.en]: "About Us" } },
			{ link: getContactPath(tenant), label: { [Locale.cs]: "Kontakt", [Locale.en]: "Contact" } },
			{ link: getPostsListingPath(tenant), label: { [Locale.cs]: "Blog", [Locale.en]: "Blog" } },
		],
	};

	const [facebookUrl, instagramUrl, youtubeUrl] = fathersContact.socialProfiles;
	const socials: { link: string; label: string; icon: string }[] = [
		{ link: facebookUrl, label: "Facebook", icon: facebookIcon },
		{ link: instagramUrl, label: "Instagram", icon: instagramIcon },
		{ link: youtubeUrl, label: "YouTube", icon: youtubeIcon },
	];

	const isDesktop = createReactiveBreakpoint(">md");

	$effect(() => {
		document.body.style.setProperty(
			"--cookiescript-badge-bottom",
			pageHasMobileBottomBar && !isDesktop.current ? "7.5rem" : "unset",
		);
	});
</script>

<footer
	class={[
		"bg-fcrdarkbrown full-width grid-container font-sans md:pt-20 md:pb-10",
		pageHasMobileBottomBar ? "pt-14 pb-38" : "py-14",
	]}
>
	<div
		class="grid md:grid-cols-[auto_11rem_11rem] lg:grid-cols-[1fr_12rem_12rem_auto] xl:grid-cols-[1fr_14.5rem_14.5rem_auto] justify-between gap-14"
	>
		<section class="flex flex-col gap-8">
			<!-- Non-MVP feature: newsletter form in the footer -->
			<!-- <NewsletterForm /> -->
			<div class="flex gap-2 md:mt-3">
				{#each socials as social (social)}
					<a
						href={social.link}
						target="_blank"
						referrerpolicy="no-referrer"
						rel="noreferrer"
						class={[
							"size-16 border-white/16 hover:border-white transition-colors",
							"flex justify-center items-center rounded-full border",
						]}
						title={social.label}
					>
						<Icon icon={social.icon} class="size-6 text-white" />
					</a>
				{/each}
			</div>
		</section>
		{#each Object.entries(footerMenu) as [sectionTitle, links] (sectionTitle)}
			<section class="flex flex-col gap-2 font-medium">
				<h2 class="text-white text-base md:text-1.5xl/8 font-antipol">
					<Formatted text={sectionTitle} />
				</h2>
				<nav>
					{#each links as linkItem (linkItem)}
						<a
							href={linkItem.link}
							class="block text-sm/[2.24] md:text-base/[2.24] text-white hover:text-fcrbrown transition-colors"
						>
							<Formatted text={linkItem.label[tenant.locale]} />
						</a>
					{/each}
				</nav>
			</section>
		{/each}
		<section class="order-last flex flex-col gap-4 -mt-4 md:mt-0 lg:order-none md:order-2">
			<div class="flex flex-col gap-3">
				<LanguageSwitcher />
				<CurrencySwitcher />
			</div>
			<DeliveryCountrySelect
				name="deliveryCountry"
				placeholder={translate("deliveryTo")}
				value={selectedDestinationCountry.current ?? defaultDestinationCountry}
				onchange={async (event: Event & { currentTarget: HTMLSelectElement }): Promise<void> => {
					await setCookie(destinationCountryCookieName, event.currentTarget.value as Country);
					location.reload();
				}}
				wrapperClass="col-span-full text-white min-w-45 max-w-full"
				class="bg-transparent border-white/16 text-white [&::picker(select)]:!border-fcrdarkgray"
			/>
		</section>
		<section class="lg:col-span-full gap-y-10 contents lg:flex justify-between flex-row items-center font-medium">
			<div
				class="flex flex-col xl:flex-row xl:items-center gap-y-2 gap-x-8.5 justify-between xl:justify-normal md:my-auto lg:my-0"
			>
				<a href={getHomepagePath(tenant)} aria-label={translate("logoAria")}>
					<Logo class="w-22 h-5 pr-px text-white" />
				</a>
				<div class="text-white/60 shrink-0">
					<Formatted text={translate("copyright")} />
				</div>
			</div>
			<div class="contents lg:flex justify-center [flex:0_0_auto] shrink-0 font-medium">
				<nav class="gap-6 flex flex-col lg:flex-row lg:mx-auto">
					{#each footerLinks as linkItem (linkItem)}
						<a
							href={linkItem.link}
							class="block text-sm/none lg:text-base/none text-white/60 hover:text-white transition-colors"
						>
							<Formatted text={linkItem.label[tenant.locale]} />
						</a>
					{/each}
				</nav>
			</div>
		</section>
	</div>
</footer>
